import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, ExternalLink, Ticket, Star, Calendar, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { AdminItem, AdminAddBar } from '../admin/AdminControls';
import type { FieldDef } from '../admin/AdminFieldModal';
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const MOVIE_FIELDS: FieldDef[] = [
  { key: 'title', label: 'Title', required: true },
  { key: 'location', label: 'Location', placeholder: 'Dallas' },
  { key: 'city', label: 'City', placeholder: 'Dallas' },
  { key: 'address', label: 'Address', type: 'textarea' },
  { key: 'theaterName', label: 'Theater name', placeholder: 'Cinemark' },
  { key: 'theaterUrl', label: 'Booking URL', type: 'url' },
  { key: 'genres', label: 'Genres (comma-separated)' },
  { key: 'duration', label: 'Duration', placeholder: '2h 45m' },
  { key: 'releaseDate', label: 'Release date', placeholder: 'Now Playing' },
  { key: 'image', label: 'Image URL', type: 'image', placeholder: 'https://…' },
  ...SOCIAL_FIELDS,
];

export interface MovieResult {
  id: string;
  title: string;
  location: string;
  city: string;
  address: string;
  rating: number;
  reviewCount: number;
  image: string;
  theaterName: string;
  theaterUrl: string;
  showtimes: string[];
  genres: string[];
  duration: string;
  releaseDate: string;
}

const LOCATION_FILTERS = ['Irving', 'Frisco', 'Plano', 'Dallas', 'Carrollton', 'McKinney', 'Lewisville', 'The Colony', 'Allen', 'Prosper', 'Aubrey', 'Little Elm', 'Melissa', 'FlowerMound', 'Other'];

const HERO_LOCATIONS = ['All Locations', 'Frisco', 'Plano', 'Irving', 'Lewisville', 'McKinney', 'Dallas', 'Carrollton', 'The Colony', 'Allen', 'Prosper', 'Aubrey', 'Little Elm', 'Melissa', 'FlowerMound', 'Other'];

const GENRES = [
  { name: 'Action', icon: '⚡' },
  { name: 'Drama', icon: '🎭' },
  { name: 'Comedy', icon: '😄' },
  { name: 'Thriller', icon: '🔥' },
  { name: 'Romance', icon: '💕' },
  { name: 'Family', icon: '👨‍👩‍👧‍👦' },
  { name: 'Telugu', icon: '🎬' },
  { name: 'Hindi', icon: '🎪' },
  { name: 'Tamil', icon: '🎭' },
];

export const MOVIES_DATA: MovieResult[] = [
  {
    id: 'movie-1',
    title: 'Peddi',
    location: 'Dallas',
    city: 'Dallas',
    address: '11819 Webb Chapel Rd, Dallas, TX 75234',
    rating: 4.8,
    reviewCount: 1250,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780872909171-u9gkx.jpg',
    theaterName: 'Cinemark',
    theaterUrl: 'https://www.cinemark.com/movies/peddi-telugu-with-english-subtitles',
    showtimes: [],
    genres: ['Telugu', 'Drama', 'Action'],
    duration: '2h 45m',
    releaseDate: 'Now Playing',
  },
  {
    id: 'movie-2',
    title: 'Hai Jawani Toh Ishq Hona Hai',
    location: 'Dallas',
    city: 'Dallas',
    address: '11819 Webb Chapel Rd, Dallas, TX 75234',
    rating: 4.8,
    reviewCount: 1250,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780872909171-u9gkx.jpg',
    theaterName: 'Cinemark',
    theaterUrl: 'https://www.cinemark.com/movies/peddi-telugu-with-english-subtitles',
    showtimes: [],
    genres: ['Hindi', 'Romance', 'Comedy'],
    duration: '2h 30m',
    releaseDate: 'Now Playing',
  },
];

export function MovieCard({ movie, index }: { movie: MovieResult; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full h-[120px] relative overflow-hidden">
        <img
          src={movie.image}
          alt={movie.title}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=600&h=400&fit=crop';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-2 left-2 right-2">
          <div className="flex items-center gap-1.5">
            <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold">
              {movie.releaseDate}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-black/50 text-white text-[10px] font-medium backdrop-blur-sm">
              {movie.duration}
            </span>
          </div>
        </div>
      </div>
      <div className="p-2 pb-0.5 flex-1">
        <h3 className="text-[14px] font-bold text-gray-900 leading-tight mb-0">{movie.title}</h3>
        <div className="flex items-center gap-1 mb-1">
          <Ticket className="w-3 h-3 text-blue-500 shrink-0" />
          <span className="text-[10px] text-gray-600 font-medium">{movie.theaterName}</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {movie.genres.map((genre) => (
            <span key={genre} className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[9px] font-semibold">
              {genre}
            </span>
          ))}
        </div>
      </div>
      <div className="px-2 pb-2 pt-0.5">
        <SocialLinks item={movie} px={20} />
        <a
          href={movie.theaterUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 bg-[#f97316] hover:bg-[#ea580c] text-white text-[11px] font-semibold py-1.5 rounded-md transition-colors duration-200"
        >
          <Ticket className="w-3 h-3" />
          Book Tickets
        </a>
      </div>
    </motion.div>
  );
}

export default function Movies() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHeroLocation, setSelectedHeroLocation] = useState('All Locations');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mc = useManagedCollection<any>('movies', MOVIES_DATA, (m, i) => slugify(m.id || m.title || String(i)));

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Normalize genres to an array (custom items may store a comma-separated string).
  const movieList = mc.items.map((m) => ({
    ...m,
    genres: Array.isArray(m.genres)
      ? m.genres
      : String(m.genres || '').split(',').map((g: string) => g.trim()).filter(Boolean),
  }));

  const effectiveLocation = selectedLocation ?? (selectedHeroLocation !== 'All Locations' ? selectedHeroLocation : null);

  const filteredMovies = movieList.filter((m) => {
    const matchesQuery = searchQuery.trim() === '' || m.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGenre = !selectedGenre || m.genres.includes(selectedGenre);
    const matchesLocation = !effectiveLocation || m.location === effectiveLocation;
    return matchesQuery && matchesGenre && matchesLocation;
  });

  const hasActiveFilters = selectedGenre || effectiveLocation;

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        .filter-scroll::-webkit-scrollbar { display: none; }
        .filter-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1600&h=600&fit=crop"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '18px', display: 'block' }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2 sm:py-0 flex items-center border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">What movie are you looking for?</span>
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="e.g. Peddi, Telugu movies, Hindi films" className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                <div ref={dropdownRef} className="relative border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0" style={{ zIndex: 99999 }}>
                  <button onClick={() => setShowDropdown(!showDropdown)} className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedHeroLocation}</span>
                    <svg className={'w-3 h-3 text-gray-400 shrink-0 transition-transform ' + (showDropdown ? 'rotate-180' : '')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showDropdown && (
                    <div
                      className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto"
                      style={{ zIndex: 99999, marginTop: '4px', width: '200px', maxHeight: '220px', boxShadow: '0 10px 24px rgba(15,23,42,0.16)', fontFamily: "'Poppins', sans-serif" }}
                    >
                      {HERO_LOCATIONS.map((loc) => (
                        <button
                          key={loc}
                          onClick={() => { setSelectedHeroLocation(loc); setShowDropdown(false); }}
                          className="w-full text-left flex items-center transition-colors duration-150"
                          style={{
                            padding: '8px 12px',
                            fontSize: '13px',
                            fontWeight: selectedHeroLocation === loc ? 700 : 500,
                            color: selectedHeroLocation === loc ? '#f97316' : '#111827',
                            background: selectedHeroLocation === loc ? '#fff3e8' : 'transparent',
                            height: '36px',
                          }}
                          onMouseEnter={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = '#fff7ed'; }}
                          onMouseLeave={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => setShowDropdown(false)} className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] sm:rounded-r-md w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* Secondary Category Nav */}
      <CategoryNav />

      {/* ── Filter Bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1320px] mx-auto px-4 pt-3 pb-2 space-y-2">

          {/* Explore by Genre */}
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-1.5" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '0.12em' }}>
              Explore by Genre
            </p>
            <div className="flex gap-2 overflow-x-auto filter-scroll pb-1 flex-wrap">
              {GENRES.map((g) => {
                const active = selectedGenre === g.name;
                return (
                  <button
                    key={g.name}
                    onClick={() => setSelectedGenre(active ? null : g.name)}
                    className="flex items-center gap-1.5 rounded-full border whitespace-nowrap transition-colors duration-150 shrink-0"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      height: '35px',
                      padding: '0 13px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      background: active ? '#dc2626' : '#ffffff',
                      color: active ? '#ffffff' : '#111827',
                      borderColor: active ? '#dc2626' : '#e5e7eb',
                      boxShadow: 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = '#fef2f2';
                        e.currentTarget.style.borderColor = '#fca5a5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <span className="text-[14px] leading-none">{g.icon}</span>
                    {g.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Explore by Location */}
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-1.5" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '0.12em' }}>
              Explore by Location
            </p>
            <div className="flex gap-2 overflow-x-auto filter-scroll pb-1 flex-wrap">
              {LOCATION_FILTERS.map((loc) => {
                const active = selectedLocation === loc;
                return (
                  <button
                    key={loc}
                    onClick={() => setSelectedLocation(active ? null : loc)}
                    className="flex items-center gap-1.5 rounded-full border whitespace-nowrap transition-colors duration-150 shrink-0"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      height: '35px',
                      padding: '0 13px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      background: active ? '#2563eb' : '#ffffff',
                      color: active ? '#ffffff' : '#111827',
                      borderColor: active ? '#2563eb' : '#e5e7eb',
                      boxShadow: 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = '#eff6ff';
                        e.currentTarget.style.borderColor = '#93c5fd';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <MapPin className="w-3 h-3 shrink-0" />
                    {loc}
                  </button>
                );
              })}

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={() => { setSelectedGenre(null); setSelectedLocation(null); }}
                  className="flex items-center gap-1.5 rounded-full border border-dashed whitespace-nowrap transition-colors duration-150 shrink-0"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    height: '35px',
                    padding: '0 13px',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: '#6b7280',
                    background: '#ffffff',
                    borderColor: '#d1d5db',
                    boxShadow: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#f97316';
                    e.currentTarget.style.borderColor = '#f97316';
                    e.currentTarget.style.background = '#fff7ed';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#6b7280';
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  <span className="text-[12px] leading-none">✕</span>
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Results ── */}
      <div className="max-w-[1320px] mx-auto px-4 py-4">
        <AdminAddBar mc={mc} fields={MOVIE_FIELDS} label="Add Movie" />
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[18px] font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {selectedLocation || selectedGenre ? 'Filtered Movies' : 'Now Playing'}
          </h2>
          <span className="text-[12px] text-gray-500 font-medium" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {filteredMovies.length} {filteredMovies.length === 1 ? 'movie' : 'movies'} found
          </span>
        </div>

        {filteredMovies.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
              <Ticket className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-gray-500 text-[13px] font-medium mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>No movies found</p>
            <p className="text-gray-400 text-[11px]" style={{ fontFamily: "'Poppins', sans-serif" }}>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredMovies.map((movie, index) => (
              <AdminItem key={(movie as any)._mcId} mc={mc} item={movie as any} fields={MOVIE_FIELDS} title="Movie">
                <MovieCard movie={movie as any} index={index} />
              </AdminItem>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
